import React, { useState } from 'react';
import { useTimelineStore } from '../store/timelineStore';

const ExportDialog = () => {
  const { showExportDialog, exportTrackId, tracks, hideExport } = useTimelineStore();
  const [exportSettings, setExportSettings] = useState({
    format: 'mp4',
    resolution: '1080p',
    fps: 30,
    quality: 'high',
    includeWatermark: true,
    exportType: 'track' // 'track' | 'selected'
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  if (!showExportDialog) return null;

  const selectedTrack = tracks.find(t => t.id === exportTrackId);

  // 处理导出设置变更
  const handleSettingChange = (key, value) => {
    setExportSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 开始导出
  const handleStartExport = async () => {
    if (!selectedTrack || selectedTrack.clips.length === 0) {
      alert('选中的轨道没有片段可导出');
      return;
    }

    setIsExporting(true);
    setExportProgress(0);

    try {
      // 获取原始视频文件路径
      const timelineState = useTimelineStore.getState();
      const videoFile = timelineState.videoFile;
      
      if (!videoFile) {
        alert('找不到原始视频文件，请重新加载视频');
        setIsExporting(false);
        return;
      }
      
      console.log('使用视频文件进行导出:', videoFile);

      // 检查是否在Electron环境中
      const isElectron = window.require && window.require('electron');
      
      if (isElectron) {
        // Electron环境：使用文件系统API
        await handleElectronExport(videoFile);
      } else {
        // 浏览器环境：生成下载链接
        await handleBrowserExport(videoFile);
      }
      
      hideExport();
      
    } catch (error) {
      console.error('导出失败:', error);
      alert('导出失败: ' + error.message);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Electron环境下的导出
  const handleElectronExport = async (videoFile) => {
    const { ipcRenderer } = window.require('electron');
    const os = window.require('os');
    const path = window.require('path');
    
    const desktopPath = path.join(os.homedir(), 'Desktop');
    const defaultFileName = `${selectedTrack.name}_导出_${new Date().toISOString().slice(0, 10)}.${exportSettings.format}`;
    
    const result = await ipcRenderer.invoke('show-save-dialog', {
      defaultPath: path.join(desktopPath, defaultFileName),
      filters: [
        { name: '视频文件', extensions: [exportSettings.format] },
        { name: '所有文件', extensions: ['*'] }
      ]
    });

    if (result.canceled) {
      setIsExporting(false);
      return;
    }

    const outputPath = result.filePath;
    console.log('导出路径:', outputPath);

    // 实际的视频导出逻辑
    await performVideoExport(videoFile, selectedTrack.clips, outputPath);
    
    // 检查文件是否成功创建
    const fs = window.require('fs');
    if (fs.existsSync(outputPath)) {
      alert(`导出成功！\n文件已保存至: ${outputPath}`);
      
      // 询问是否打开文件夹
      const openFolder = window.confirm('是否打开文件所在文件夹？');
      if (openFolder) {
        const { shell } = window.require('electron');
        shell.showItemInFolder(outputPath);
      }
    } else {
      throw new Error('导出文件未能成功创建');
    }
  };

  // 浏览器环境下的导出 - 使用Web API进行视频合成
  const handleBrowserExport = async (videoFile) => {
    try {
      setExportProgress(10);
      
      // 检查浏览器是否支持必要的API
      if (!window.MediaRecorder || !window.OffscreenCanvas) {
        // 如果不支持，回退到信息导出
        await handleFallbackExport();
        return;
      }
      
      setExportProgress(20);
      
      // 创建视频元素用于处理
      const video = document.createElement('video');
      video.src = videoFile;
      video.crossOrigin = 'anonymous';
      
      // 等待视频加载
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
      });
      
      setExportProgress(30);
      
      // 创建canvas用于视频处理
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // 设置canvas尺寸
      const width = video.videoWidth;
      const height = video.videoHeight;
      canvas.width = width;
      canvas.height = height;
      
      setExportProgress(40);
      
      // 创建MediaRecorder进行录制
      const stream = canvas.captureStream(30); // 30 FPS
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9'
      });
      
      const recordedChunks = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunks.push(event.data);
        }
      };
      
      // 开始录制
      mediaRecorder.start();
      
      setExportProgress(50);
      
      // 按顺序播放和录制每个片段
      for (let i = 0; i < selectedTrack.clips.length; i++) {
        const clip = selectedTrack.clips[i];
        
        console.log(`处理片段 ${i + 1}/${selectedTrack.clips.length}: ${clip.title}`);
        
        // 跳转到片段开始时间
        video.currentTime = clip.startTime;
        
        // 等待视频跳转完成
        await new Promise(resolve => {
          video.onseeked = resolve;
        });
        
        // 录制片段
        await recordClipSegment(video, canvas, ctx, clip, selectedTrack);
        
        // 更新进度
        const progress = 50 + (40 * (i + 1) / selectedTrack.clips.length);
        setExportProgress(progress);
      }
      
      // 停止录制
      mediaRecorder.stop();
      
      // 等待录制完成
      await new Promise(resolve => {
        mediaRecorder.onstop = resolve;
      });
      
      setExportProgress(95);
      
      // 创建最终的视频blob
      const finalBlob = new Blob(recordedChunks, { type: 'video/webm' });
      
      // 创建下载链接
      const url = URL.createObjectURL(finalBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTrack.name}_合成视频_${new Date().toISOString().slice(0, 10)}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setExportProgress(100);
      
      alert(`视频合成完成！\n\n合成的视频文件已下载到您的下载文件夹。\n文件名: ${a.download}\n\n注意: 由于浏览器限制，导出格式为WebM，如需MP4格式请使用在线转换工具。`);
      
    } catch (error) {
      console.error('浏览器视频合成失败:', error);
      alert('视频合成失败，将使用备用方案导出片段信息');
      await handleFallbackExport();
    }
  };
  
  // 录制单个片段
  const recordClipSegment = async (video, canvas, ctx, clip, track) => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const duration = clip.duration * 1000; // 转换为毫秒
      
      const drawFrame = () => {
        const elapsed = Date.now() - startTime;
        
        if (elapsed >= duration) {
          resolve();
          return;
        }
        
        // 清除canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // 绘制视频帧
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // 添加水印（如果需要）
        if (exportSettings.includeWatermark && track.watermark) {
          ctx.save();
          ctx.font = '24px Arial';
          ctx.fillStyle = track.color;
          ctx.globalAlpha = 0.6;
          ctx.fillText(track.watermark, 20, canvas.height - 30);
          ctx.restore();
        }
        
        // 添加片段标题
        ctx.save();
        ctx.font = '16px Arial';
        ctx.fillStyle = 'white';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.8;
        const text = clip.title;
        ctx.strokeText(text, 20, 40);
        ctx.fillText(text, 20, 40);
        ctx.restore();
        
        // 更新视频时间
        const videoTime = clip.startTime + (elapsed / 1000);
        if (videoTime < clip.endTime) {
          video.currentTime = videoTime;
        }
        
        requestAnimationFrame(drawFrame);
      };
      
      drawFrame();
    });
  };
  
  // 备用导出方案（当浏览器不支持视频合成时）
  const handleFallbackExport = async () => {
    const clipInfo = selectedTrack.clips.map((clip, index) => {
      return `片段 ${index + 1}: ${clip.title}
开始时间: ${formatTime(clip.startTime)}
结束时间: ${formatTime(clip.endTime)}
时长: ${formatTime(clip.duration)}
类型: ${clip.type === 'offense' ? '进攻' : '防守'}
描述: ${clip.description || '无'}
---`;
    }).join('\n\n');
    
    const exportInfo = `篮球战术剪辑器 - 轨道导出信息
==============================
轨道名称: ${selectedTrack.name}
导出时间: ${new Date().toLocaleString()}
片段总数: ${selectedTrack.clips.length}
总时长: ${formatTime(selectedTrack.clips.reduce((sum, clip) => sum + clip.duration, 0))}

导出设置:
- 格式: ${exportSettings.format}
- 分辨率: ${exportSettings.resolution}
- 帧率: ${exportSettings.fps} FPS
- 质量: ${exportSettings.quality}
- 包含水印: ${exportSettings.includeWatermark ? '是' : '否'}

片段详情:
==============================

${clipInfo}

浏览器环境说明:
==============================
您的浏览器不支持视频合成功能。
建议使用以下方法获得剪辑后的视频：

方法1: 使用现代浏览器
- Chrome 88+、Firefox 85+、Safari 14+ 支持视频合成
- 更新浏览器后重新尝试导出

方法2: 使用专业视频编辑软件
- 根据上述时间信息，在视频编辑软件中精确剪辑
- 推荐软件：Adobe Premiere Pro、Final Cut Pro、DaVinci Resolve

方法3: 使用在线视频编辑工具
- 上传原视频到在线编辑平台
- 根据时间信息进行剪辑

方法4: 下载桌面版应用
- 使用Electron桌面版本可以进行真正的视频剪辑
- 支持FFmpeg自动剪辑和合并

使用说明:
1. 在视频播放器中跳转到对应时间点查看片段内容
2. 使用视频编辑软件根据时间信息进行精确剪辑
3. 如需水印，请在编辑软件中添加文字水印
`;
    
    // 创建下载链接
    const blob = new Blob([exportInfo], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTrack.name}_片段信息_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    alert(`导出完成！\n\n片段信息文件已下载到您的下载文件夹。\n\n您的浏览器不支持视频合成功能，请参考导出的文件说明使用其他方法进行视频剪辑。`);
  };

  // 实际的视频导出功能
  const performVideoExport = async (sourceVideoPath, clips, outputPath) => {
    const fs = window.require('fs');
    const path = window.require('path');
    const { spawn } = window.require('child_process');
    
    try {
      console.log('开始导出视频...');
      console.log('源视频:', sourceVideoPath);
      console.log('输出路径:', outputPath);
      console.log('片段数量:', clips.length);
      
      setExportProgress(10);
      
      // 检查是否有FFmpeg
      const hasFFmpeg = await checkFFmpeg();
      if (!hasFFmpeg) {
        // 如果没有FFmpeg，使用简化版导出
        console.log('未找到FFmpeg，使用简化版导出');
        await exportSimplified(sourceVideoPath, clips, outputPath);
        return;
      }
      
      // 使用FFmpeg进行真正的视频剪辑
      await exportWithFFmpeg(sourceVideoPath, clips, outputPath);
      
    } catch (error) {
      console.error('视频导出过程中出错:', error);
      throw error;
    }
  };

  // 检查FFmpeg是否可用
  const checkFFmpeg = () => {
    return new Promise((resolve) => {
      const { spawn } = window.require('child_process');
      const ffmpeg = spawn('ffmpeg', ['-version']);
      
      ffmpeg.on('error', () => {
        resolve(false);
      });
      
      ffmpeg.on('close', (code) => {
        resolve(code === 0);
      });
      
      // 设置超时
      setTimeout(() => resolve(false), 3000);
    });
  };

  // 使用FFmpeg进行真正的视频剪辑
  const exportWithFFmpeg = async (sourceVideoPath, clips, outputPath) => {
    const fs = window.require('fs');
    const path = window.require('path');
    const { spawn } = window.require('child_process');
    const os = window.require('os');
    
    try {
      setExportProgress(20);
      
      // 检查源文件是否存在
      if (!fs.existsSync(sourceVideoPath)) {
        throw new Error(`源视频文件不存在: ${sourceVideoPath}`);
      }
      
      // 创建输出目录
      const outputDir = path.dirname(outputPath);
      if (!fs.existsSync(outputDir)) {
        fs.mkdirSync(outputDir, { recursive: true });
      }
      
      setExportProgress(30);
      
      // 创建临时目录用于存储片段
      const tempDir = path.join(os.tmpdir(), `basketball_clips_${Date.now()}`);
      fs.mkdirSync(tempDir, { recursive: true });
      
      console.log('临时目录:', tempDir);
      
      // 逐个剪辑片段
      const clipPaths = [];
      for (let i = 0; i < clips.length; i++) {
        const clip = clips[i];
        const clipPath = path.join(tempDir, `clip_${i.toString().padStart(3, '0')}.${exportSettings.format}`);
        
        console.log(`剪辑片段 ${i + 1}/${clips.length}: ${clip.title}`);
        
        await cutVideoSegment(sourceVideoPath, clip, clipPath);
        clipPaths.push(clipPath);
        
        // 更新进度 (30% + 50% * progress)
        const progress = 30 + (50 * (i + 1) / clips.length);
        setExportProgress(progress);
      }
      
      setExportProgress(80);
      
      // 合并所有片段
      console.log('合并视频片段...');
      await mergeVideoSegments(clipPaths, outputPath);
      
      setExportProgress(90);
      
      // 如果需要添加水印
      if (exportSettings.includeWatermark && selectedTrack.watermark) {
        console.log('添加水印...');
        const watermarkedPath = outputPath.replace(/\.[^.]+$/, '_watermarked.' + exportSettings.format);
        await addWatermark(outputPath, watermarkedPath, selectedTrack.watermark, selectedTrack.color);
        
        // 替换原文件
        fs.unlinkSync(outputPath);
        fs.renameSync(watermarkedPath, outputPath);
      }
      
      // 清理临时文件
      console.log('清理临时文件...');
      clipPaths.forEach(clipPath => {
        if (fs.existsSync(clipPath)) {
          fs.unlinkSync(clipPath);
        }
      });
      fs.rmdirSync(tempDir);
      
      setExportProgress(100);
      console.log('视频剪辑导出完成');
      
    } catch (error) {
      console.error('FFmpeg导出失败:', error);
      throw error;
    }
  };

  // 剪辑单个视频片段
  const cutVideoSegment = (inputPath, clip, outputPath) => {
    return new Promise((resolve, reject) => {
      const { spawn } = window.require('child_process');
      
      const args = [
        '-i', inputPath,
        '-ss', clip.startTime.toString(),
        '-t', clip.duration.toString(),
        '-c', 'copy', // 使用流复制，速度更快
        '-avoid_negative_ts', 'make_zero',
        outputPath
      ];
      
      console.log('FFmpeg命令:', 'ffmpeg', args.join(' '));
      
      const ffmpeg = spawn('ffmpeg', args);
      
      ffmpeg.stderr.on('data', (data) => {
        console.log('FFmpeg输出:', data.toString());
      });
      
      ffmpeg.on('close', (code) => {
        if (code === 0) {
          console.log(`片段剪辑完成: ${outputPath}`);
          resolve();
        } else {
          reject(new Error(`FFmpeg剪辑失败，退出码: ${code}`));
        }
      });
      
      ffmpeg.on('error', (error) => {
        reject(new Error(`FFmpeg执行错误: ${error.message}`));
      });
    });
  };

  // 合并视频片段
  const mergeVideoSegments = (clipPaths, outputPath) => {
    return new Promise((resolve, reject) => {
      const { spawn } = window.require('child_process');
      const fs = window.require('fs');
      const path = window.require('path');
      const os = window.require('os');
      
      // 创建文件列表
      const listPath = path.join(os.tmpdir(), `filelist_${Date.now()}.txt`);
      const fileList = clipPaths.map(p => `file '${p}'`).join('\n');
      fs.writeFileSync(listPath, fileList);
      
      const args = [
        '-f', 'concat',
        '-safe', '0',
        '-i', listPath,
        '-c', 'copy',
        outputPath
      ];
      
      console.log('FFmpeg合并命令:', 'ffmpeg', args.join(' '));
      
      const ffmpeg = spawn('ffmpeg', args);
      
      ffmpeg.stderr.on('data', (data) => {
        console.log('FFmpeg合并输出:', data.toString());
      });
      
      ffmpeg.on('close', (code) => {
        // 清理文件列表
        if (fs.existsSync(listPath)) {
          fs.unlinkSync(listPath);
        }
        
        if (code === 0) {
          console.log('视频合并完成');
          resolve();
        } else {
          reject(new Error(`FFmpeg合并失败，退出码: ${code}`));
        }
      });
      
      ffmpeg.on('error', (error) => {
        reject(new Error(`FFmpeg合并执行错误: ${error.message}`));
      });
    });
  };

  // 添加水印
  const addWatermark = (inputPath, outputPath, watermarkText, color) => {
    return new Promise((resolve, reject) => {
      const { spawn } = window.require('child_process');
      
      // 根据颜色设置水印颜色
      const fontColor = color === '#4CAF50' ? 'green' : 'red';
      
      const args = [
        '-i', inputPath,
        '-vf', `drawtext=text='${watermarkText}':fontcolor=${fontColor}:fontsize=24:x=10:y=h-th-10:alpha=0.6`,
        '-c:a', 'copy',
        outputPath
      ];
      
      console.log('FFmpeg水印命令:', 'ffmpeg', args.join(' '));
      
      const ffmpeg = spawn('ffmpeg', args);
      
      ffmpeg.stderr.on('data', (data) => {
        console.log('FFmpeg水印输出:', data.toString());
      });
      
      ffmpeg.on('close', (code) => {
        if (code === 0) {
          console.log('水印添加完成');
          resolve();
        } else {
          reject(new Error(`FFmpeg水印添加失败，退出码: ${code}`));
        }
      });
      
      ffmpeg.on('error', (error) => {
        reject(new Error(`FFmpeg水印执行错误: ${error.message}`));
      });
    });
  };

  // 简化版导出实现（当没有FFmpeg时使用）
  const exportSimplified = async (sourceVideoPath, clips, outputPath) => {
    const fs = window.require('fs');
    const path = window.require('path');
    
    try {
      setExportProgress(20);
      
      // 检查源文件是否存在
      if (!fs.existsSync(sourceVideoPath)) {
        throw new Error(`源视频文件不存在: ${sourceVideoPath}`);
      }
      
      setExportProgress(30);
      
      // 创建输出目录（如果不存在）
      const outputDir = path.dirname(outputPath);
      if (!fs.existsSync(outputDir)) {
        fs.mkdirSync(outputDir, { recursive: true });
      }
      
      setExportProgress(40);
      
      // 复制原视频文件到输出位置
      console.log('复制视频文件...');
      fs.copyFileSync(sourceVideoPath, outputPath);
      
      setExportProgress(60);
      
      // 创建片段信息文件
      const infoPath = outputPath.replace(/\.[^.]+$/, '_片段信息.txt');
      const clipInfo = clips.map((clip, index) => {
        return `片段 ${index + 1}: ${clip.title}
开始时间: ${formatTime(clip.startTime)}
结束时间: ${formatTime(clip.endTime)}
时长: ${formatTime(clip.duration)}
类型: ${clip.type === 'offense' ? '进攻' : '防守'}
描述: ${clip.description || '无'}
---`;
      }).join('\n\n');
      
      const fullInfo = `篮球战术剪辑器 - 轨道导出信息
==============================
轨道名称: ${selectedTrack.name}
导出时间: ${new Date().toLocaleString()}
片段总数: ${clips.length}
总时长: ${formatTime(clips.reduce((sum, clip) => sum + clip.duration, 0))}

导出设置:
- 格式: ${exportSettings.format}
- 分辨率: ${exportSettings.resolution}
- 帧率: ${exportSettings.fps} FPS
- 质量: ${exportSettings.quality}
- 包含水印: ${exportSettings.includeWatermark ? '是' : '否'}

片段详情:
==============================

${clipInfo}

注意: 由于系统未安装FFmpeg，这是一个简化版导出，包含了完整的原视频文件。
要获得只包含选定片段的视频，请：
1. 安装FFmpeg后重新导出
2. 或使用专业的视频编辑软件根据时间信息进行精确剪辑

FFmpeg安装说明:
- macOS: brew install ffmpeg
- Windows: 从 https://ffmpeg.org/download.html 下载
- Linux: sudo apt install ffmpeg

使用说明:
1. 根据上述时间信息，在视频播放器中跳转到对应时间点
2. 或使用视频编辑软件根据时间信息进行精确剪辑
`;
      
      fs.writeFileSync(infoPath, fullInfo, 'utf8');
      
      setExportProgress(80);
      
      // 如果需要水印，创建水印说明文件
      if (exportSettings.includeWatermark && selectedTrack.watermark) {
        const watermarkPath = outputPath.replace(/\.[^.]+$/, '_水印说明.txt');
        const watermarkInfo = `水印信息
========
轨道: ${selectedTrack.name}
水印文字: ${selectedTrack.watermark}
位置: 左下角
透明度: 60%
颜色: ${selectedTrack.color === '#4CAF50' ? '绿色' : '红色'}

建议在视频编辑软件中添加此水印。
`;
        fs.writeFileSync(watermarkPath, watermarkInfo, 'utf8');
      }
      
      setExportProgress(100);
      
      console.log('简化版导出完成');
      
    } catch (error) {
      console.error('简化导出失败:', error);
      throw error;
    }
  };

  // 格式化时间显示
  const formatTime = (time) => {
    if (!time || isNaN(time)) return '00:00.000';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const milliseconds = Math.floor((time % 1) * 1000);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };


  // 取消导出
  const handleCancel = () => {
    if (isExporting) {
      if (window.confirm('正在导出中，确定要取消吗？')) {
        setIsExporting(false);
        setExportProgress(0);
        hideExport();
      }
    } else {
      hideExport();
    }
  };

  // 获取预估文件大小
  const getEstimatedSize = () => {
    if (!selectedTrack) return '未知';
    
    const totalDuration = selectedTrack.clips.reduce((sum, clip) => sum + clip.duration, 0);
    const { resolution, quality } = exportSettings;
    
    // 简单的大小估算（实际应该更复杂）
    let bitrate = 5000; // kbps
    if (resolution === '720p') bitrate = 3000;
    if (quality === 'medium') bitrate *= 0.7;
    if (quality === 'low') bitrate *= 0.5;
    
    const sizeInMB = (totalDuration * bitrate) / 8 / 1024;
    return `约 ${Math.round(sizeInMB)} MB`;
  };

  return (
    <div className="export-dialog-overlay">
      <div className="export-dialog">
        <div className="dialog-header">
          <h3>导出轨道</h3>
          <button className="btn-close" onClick={handleCancel}>×</button>
        </div>

        <div className="dialog-content">
          {/* 轨道信息 */}
          <div className="export-info">
            <h4>轨道信息</h4>
            <div className="info-grid">
              <div>轨道名称: {selectedTrack?.name}</div>
              <div>片段数量: {selectedTrack?.clips.length || 0}</div>
              <div>总时长: {Math.round(selectedTrack?.clips.reduce((sum, clip) => sum + clip.duration, 0) || 0)}秒</div>
              <div>预估大小: {getEstimatedSize()}</div>
            </div>
          </div>

          {/* 导出设置 */}
          <div className="export-settings">
            <h4>导出设置</h4>
            
            <div className="setting-group">
              <label>视频格式:</label>
              <select 
                value={exportSettings.format}
                onChange={(e) => handleSettingChange('format', e.target.value)}
                disabled={isExporting}
              >
                <option value="mp4">MP4 (推荐)</option>
                <option value="mov">MOV</option>
                <option value="avi">AVI</option>
              </select>
            </div>

            <div className="setting-group">
              <label>分辨率:</label>
              <select 
                value={exportSettings.resolution}
                onChange={(e) => handleSettingChange('resolution', e.target.value)}
                disabled={isExporting}
              >
                <option value="1080p">1080p (1920×1080)</option>
                <option value="720p">720p (1280×720)</option>
                <option value="480p">480p (854×480)</option>
              </select>
            </div>

            <div className="setting-group">
              <label>帧率:</label>
              <select 
                value={exportSettings.fps}
                onChange={(e) => handleSettingChange('fps', parseInt(e.target.value))}
                disabled={isExporting}
              >
                <option value={30}>30 FPS</option>
                <option value={60}>60 FPS</option>
                <option value={24}>24 FPS</option>
              </select>
            </div>

            <div className="setting-group">
              <label>质量:</label>
              <select 
                value={exportSettings.quality}
                onChange={(e) => handleSettingChange('quality', e.target.value)}
                disabled={isExporting}
              >
                <option value="high">高质量</option>
                <option value="medium">中等质量</option>
                <option value="low">低质量</option>
              </select>
            </div>

            <div className="setting-group">
              <label>
                <input
                  type="checkbox"
                  checked={exportSettings.includeWatermark}
                  onChange={(e) => handleSettingChange('includeWatermark', e.target.checked)}
                  disabled={isExporting}
                />
                包含水印
              </label>
            </div>
          </div>

          {/* 导出进度 */}
          {isExporting && (
            <div className="export-progress">
              <h4>导出进度</h4>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${exportProgress}%` }}
                />
              </div>
              <div className="progress-text">
                {Math.round(exportProgress)}% 完成
              </div>
            </div>
          )}

          {/* 片段列表预览 */}
          <div className="export-preview">
            <h4>将要导出的片段</h4>
            <div className="clips-preview">
              {selectedTrack?.clips.map((clip, index) => (
                <div key={clip.id} className="preview-clip">
                  <span className="clip-index">{index + 1}</span>
                  <span className="clip-title">{clip.title}</span>
                  <span className="clip-duration">
                    {Math.round(clip.duration * 100) / 100}s
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="dialog-actions">
          <button 
            className="btn-export"
            onClick={handleStartExport}
            disabled={isExporting || !selectedTrack || selectedTrack.clips.length === 0}
          >
            {isExporting ? '导出中...' : '开始导出'}
          </button>
          <button 
            className="btn-cancel"
            onClick={handleCancel}
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDialog;